import { ApiClientProvider } from "@/api/ApiClientProvider";
import { SessionExpiredNotice } from "@/authentication/SuperTokensProvider";
import { AskNobiProvider } from "@/components/AskNobi/AskNobiProvider";
import { FlashToastProvider } from "@/components/FlashToast/FlashToast";
import { AppVersionUpdateManager } from "@/components/ProductUpdateManagers/ProductUpdateManagers";
import { ProductUpdatesProvider } from "@/components/ProductUpdates/ProductUpdatesProvider";
import { TranslationInspector } from "@/components/TranslationInspector/TranslationInspector";
import { useClearTicketsCache } from "@/modules/tickets/filters";
import { ConfigNoUserProvider } from "@/providers/config/ConfigProvider";
import { ForbiddenPopupProvider } from "@/providers/ForbiddenPopupProvider";
import { IntercomIntegration } from "@/providers/IntercomIntegration";
import { SessionIdentity } from "@/providers/SessionIdentity";
import { TranslationsProvider } from "@/translations/TranslationsProvider";

/** Anonymous bootstrap shared by all routes; translations can load before session identity. */
export function GlobalProviders({ children }: { children: React.ReactNode }): React.ReactNode {
  return (
    <FlashToastProvider>
      <ConfigNoUserProvider>
        <ApiClientProvider>
          <TranslationsProvider>{children}</TranslationsProvider>
        </ApiClientProvider>
      </ConfigNoUserProvider>
    </FlashToastProvider>
  );
}

/** UI services consume the active route's dictionary, including after user-language reconciliation. */
export function LocalizedAppProviders({ children }: { children: React.ReactNode }): React.ReactNode {
  return (
    <>
      <ForbiddenPopupProvider>{children}</ForbiddenPopupProvider>
      <AppVersionUpdateManager />
      <SessionExpiredNotice />
      <TranslationInspector />
    </>
  );
}

/** Dashboard UI contexts and nonblocking integrations. The route shell owns project/self readiness. */
export function LoggedInProviders({ children }: { children: React.ReactNode }): React.ReactNode {
  useClearTicketsCache();

  return (
    <LocalizedAppProviders>
      <SessionIdentity />
      <IntercomIntegration />
      <ProductUpdatesProvider>
        <AskNobiProvider>{children}</AskNobiProvider>
      </ProductUpdatesProvider>
    </LocalizedAppProviders>
  );
}
